<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import Avatar from 'next/avatar/Avatar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  scheduledMessage: { type: Object, required: true },
  isSending: { type: Boolean, default: false },
  isCancelling: { type: Boolean, default: false },
});

const emit = defineEmits(['edit', 'cancel', 'send-now']);

const { t, locale } = useI18n();

const dateLocale = computed(() => (locale.value === 'pt_BR' ? ptBR : enUS));

const formattedScheduledAt = computed(() => {
  const timestamp = props.scheduledMessage.scheduled_at;
  // Handle both Unix timestamp (seconds) and ISO string formats
  const date =
    typeof timestamp === 'number'
      ? new Date(timestamp * 1000)
      : new Date(timestamp);
  return format(date, 'dd/MM HH:mm', { locale: dateLocale.value });
});

const recurrenceLabel = computed(() => {
  const type = props.scheduledMessage.recurrence_type;
  if (!type) return t('CONVERSATION.SCHEDULED_MESSAGE.ONCE');
  return t(`CONVERSATION.SCHEDULED_MESSAGE.RECURRENCE.${type.toUpperCase()}`);
});
</script>

<template>
  <div
    class="border-l-4 border-n-amber-9 rounded-lg p-2.5 bg-n-slate-3 dark:bg-n-slate-4"
  >
    <!-- Content -->
    <div
      class="text-sm text-n-slate-12 mb-1.5 whitespace-pre-wrap line-clamp-3"
    >
      {{
        scheduledMessage.content ||
        $t('CONVERSATION.SCHEDULED_MESSAGE.ATTACHMENT_ONLY')
      }}
    </div>

    <!-- Attachments -->
    <div
      v-if="scheduledMessage.has_attachments"
      class="flex items-center gap-1 text-n-slate-11 text-xs mb-1.5"
    >
      <Icon icon="i-ph-paperclip" class="size-3" />
      {{ scheduledMessage.attachments_count }}
    </div>

    <!-- Meta (linha compacta com separadores) -->
    <div
      class="flex items-center gap-1.5 text-xs text-n-slate-11 mb-1.5 border-t border-n-amber-9 pt-1.5"
    >
      <Icon icon="i-ph-clock" class="size-3" />
      <span>{{ formattedScheduledAt }}</span>
      <span class="text-n-amber-9">·</span>
      <Icon icon="i-ph-repeat" class="size-3" />
      <span>{{ recurrenceLabel }}</span>
      <span class="text-n-amber-9">·</span>
      <Avatar :name="scheduledMessage.sender?.name" :size="12" />
      <span class="truncate max-w-20">{{ scheduledMessage.sender?.name }}</span>
    </div>

    <!-- Actions -->
    <div class="flex gap-2 justify-end">
      <NextButton
        :label="$t('CONVERSATION.SCHEDULED_MESSAGE.SEND_NOW')"
        icon="i-ph-paper-plane-tilt"
        blue
        faded
        xs
        :is-loading="isSending"
        :disabled="isSending || isCancelling"
        @click="emit('send-now', scheduledMessage)"
      />
      <NextButton
        :label="$t('CONVERSATION.SCHEDULED_MESSAGE.CANCEL')"
        icon="i-ph-x"
        ruby
        faded
        xs
        :is-loading="isCancelling"
        :disabled="isSending || isCancelling"
        @click="emit('cancel', scheduledMessage)"
      />
      <NextButton
        :label="$t('CONVERSATION.SCHEDULED_MESSAGE.EDIT')"
        icon="i-ph-pencil-simple"
        slate
        faded
        xs
        @click="emit('edit', scheduledMessage)"
      />
    </div>
  </div>
</template>
